import React, { useState, useCallback } from 'react';
import { Plus, Eye, Truck, UserCheck, CheckCircle2, Circle, XCircle, Clock, FileText, Image, Receipt, ChevronLeft, ChevronRight, MapPin, Phone, Package, IndianRupee, X, FileImage } from 'lucide-react';
import InvoiceModal from '../components/InvoiceModal';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import FilterBar from '../components/FilterBar';
import { StatusBadge } from '../components/StatusBadge';
import { useFetch } from '../hooks';
import { orderService, deliveryBoyService } from '../services/api';
import { formatDate, formatCurrency } from '../utils/helpers';

/* ── Constants ──────────────────────────────────────────────────────────────── */
const ORDER_STATUSES = ['placed', 'approved', 'assigned', 'out-for-delivery', 'delivered', 'cancelled', 'delivery-failed'];
const STATUS_FLOW    = ['placed', 'approved', 'assigned', 'out-for-delivery', 'delivered'];

// API se aane wale status values ko normalize karo
const normalizeStatus = (s) => {
  if (!s) return s;
  if (s === 'confirmed')        return 'assigned';
  if (s === 'out_for_delivery') return 'out-for-delivery';
  if (s === 'delivery_failed')  return 'delivery-failed';
  return s;
};

const STATUS_ICONS = {
  placed:             <Circle className="w-4 h-4" />,
  approved:           <CheckCircle2 className="w-4 h-4" />,
  assigned:           <Truck className="w-4 h-4" />,
  'out-for-delivery': <Truck className="w-4 h-4" />,
  delivered:          <CheckCircle2 className="w-4 h-4" />,
  cancelled:          <XCircle className="w-4 h-4" />,
  'delivery-failed':  <XCircle className="w-4 h-4" />,
};

const STATUS_COLORS = {
  placed:             'bg-sky-500 text-white',
  approved:           'bg-indigo-500 text-white',
  assigned:           'bg-violet-500 text-white',
  'out-for-delivery': 'bg-orange-500 text-white',
  delivered:          'bg-green-500 text-white',
  cancelled:          'bg-red-500 text-white',
  'delivery-failed':  'bg-rose-600 text-white',
};

const getAllowedStatuses = (currentStatus) => {
  if (currentStatus === 'assigned')           return ['out-for-delivery'];
  if (currentStatus === 'out-for-delivery')   return ['delivered', 'delivery-failed'];
  return [];
};

/* ── Delivery Timeline ───────────────────────────────────────────────────── */
const DeliveryTimeline = ({ currentStatus }) => {
  const isCancelled = currentStatus === 'cancelled';
  const currentIdx  = STATUS_FLOW.indexOf(currentStatus);
  return (
    <div>
      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Delivery Timeline</p>
      <div className="flex items-center gap-0 overflow-x-auto pb-1">
        {STATUS_FLOW.map((step, i) => {
          const stepIdx  = STATUS_FLOW.indexOf(step);
          const isDone   = !isCancelled && stepIdx <= currentIdx;
          const isActive = !isCancelled && step === currentStatus;
          return (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  isActive ? STATUS_COLORS[step] :
                  isDone   ? 'bg-green-500 text-white' :
                  isCancelled && step === 'placed' ? 'bg-red-500 text-white' :
                  'bg-gray-200 dark:bg-gray-700 text-gray-400'
                }`}>
                  {STATUS_ICONS[step]}
                </div>
                <span className={`text-xs mt-1 whitespace-nowrap capitalize ${
                  isActive ? 'font-semibold text-gray-900 dark:text-white' :
                  isDone   ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                }`}>{step.replace(/-/g, ' ')}</span>
              </div>
              {i < STATUS_FLOW.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 min-w-[16px] ${
                  !isCancelled && stepIdx < currentIdx ? 'bg-green-400' : 'bg-gray-200 dark:bg-gray-700'
                }`} />
              )}
            </React.Fragment>
          );
        })}
        {isCancelled && (
          <>
            <div className="flex-1 h-0.5 mx-1 min-w-[16px] bg-red-300" />
            <div className="flex flex-col items-center shrink-0">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-red-500 text-white">
                <XCircle className="w-4 h-4" />
              </div>
              <span className="text-xs mt-1 font-semibold text-red-500">Cancelled</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Assign Delivery Form ────────────────────────────────────────────────── */
const AssignDeliveryForm = ({ order, deliveryBoys, onSubmit, onClose }) => {
  const rules = { deliveryBoyId: { required: true } };
  const { values, errors, handleChange, handleBlur, handleSubmit } = useForm({
    deliveryBoyId: order.delivery_boy_id ? String(order.delivery_boy_id) : '',
  }, rules);

  const selectedBoy = deliveryBoys?.find(b => b.id === parseInt(values.deliveryBoyId));

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl border border-gray-200 dark:border-gray-600">
        <div className="w-9 h-9 bg-medihub-light dark:bg-medihub-dark/30 rounded-lg flex items-center justify-center shrink-0">
          <Truck className="w-4 h-4 text-medihub-primary dark:text-medihub-highlight" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{order.uid}</p>
          <p className="text-xs text-gray-500">{order.user_name} · {formatCurrency(order.total_amount)}</p>
        </div>
        <StatusBadge status={order.status} dot={false} />
      </div>

      <FormField label="Delivery Boy" error={errors.deliveryBoyId} required>
        <select name="deliveryBoyId" value={values.deliveryBoyId} onChange={handleChange} onBlur={handleBlur} className="input-field">
          <option value="">Select delivery boy…</option>
          {deliveryBoys?.map(b => (
            <option key={b.id} value={b.id}>{b.name} — {b.mobile}</option>
          ))}
        </select>
      </FormField>

      {selectedBoy && (
        <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
          <div className="w-9 h-9 bg-green-100 dark:bg-green-900/40 rounded-xl flex items-center justify-center text-green-700 dark:text-green-400 font-bold text-sm shrink-0">
            {selectedBoy.name[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedBoy.name}</p>
            <p className="text-xs text-gray-500">{selectedBoy.mobile}</p>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary flex items-center gap-1.5">
          <Truck className="w-4 h-4" />
          {order.delivery_boy_id ? 'Reassign' : 'Assign'} Delivery
        </button>
      </div>
    </Form>
  );
};

/* ── Filter config ───────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', status: '', payment_method: '', dateFrom: '', dateTo: '' };
const FILTER_FIELDS = [
  {
    key: 'status', label: 'Order Status', type: 'select',
    options: ORDER_STATUSES.map(s => ({ value: s, label: s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) })),
  },
  {
    key: 'payment_method', label: 'Payment', type: 'select',
    options: [{ value: 'cod', label: 'COD' }, { value: 'online', label: 'Online' }],
  },
  { key: 'dateFrom', label: 'From Date', type: 'date' },
  { key: 'dateTo',   label: 'To Date',   type: 'date' },
];

const orderFilterFn = (o, f) => {
  if (f.search) {
    const q = f.search.toLowerCase();
    if (![o.uid, o.user_name, o.user_mobile, o.mobile].join(' ').toLowerCase().includes(q)) return false;
  }
  if (f.status         && o.status         !== f.status)         return false;
  if (f.payment_method && o.payment_method !== f.payment_method) return false;
  const date = o.created_at?.split(' ')[0];
  if (f.dateFrom && date < f.dateFrom) return false;
  if (f.dateTo   && date > f.dateTo)   return false;
  return true;
};

/* ── Page ────────────────────────────────────────────────────────────────── */
const Orders = () => {
  const [page, setPage] = useState(1);
  const { data: result, loading, refetch } = useFetch(() => orderService.getAll({ page }), [page]);
  const { data: activeDeliveryBoys } = useFetch(() => deliveryBoyService.getAll());

  const orders = (result?.orders || []).filter(o => !o.items?.length || !o.items.every(item => item.type === 'lab_test')).map(o => ({ ...o, status: normalizeStatus(o.status) }));
  const pagination = result?.pagination || {};

  const [selected, setSelected] = useState(null);
  const [modal, setModal]       = useState(null);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [codForm, setCodForm] = useState({ delivery_boy_id: '', amount: '', order_ids: '', notes: '' });
  const [codHistoryBoyId, setCodHistoryBoyId] = useState('');
  const { data: codHistory, refetch: refetchCodHistory } = useFetch(
    () => codHistoryBoyId ? orderService.getCODCollectionHistory(codHistoryBoyId) : Promise.resolve([]),
    [codHistoryBoyId]
  );

  const filterFnCb = useCallback(orderFilterFn, []);
  const [filters, setFilters] = useState(FILTER_INITIAL);
  const onFilterChange = (key, val) => setFilters(p => ({ ...p, [key]: val }));
  const onReset = () => setFilters(FILTER_INITIAL);
  const activeCount = Object.values(filters).filter(Boolean).length;

  const filtered = orders.filter(o => orderFilterFn(o, filters));

  const [failedReason, setFailedReason] = useState('');

  const toApiStatus = (s) => s.replace(/-/g, '_');

  const handleStatusUpdate = async (id, status) => {
    if (status === 'delivery-failed') { setModal('delivery-failed'); return; }
    try {
      await orderService.updateStatus(id, toApiStatus(status));
      toast.success('Order status updated!');
      refetch();
      setSelected(prev => prev ? { ...prev, status } : prev);
    } catch { toast.error('Failed to update'); }
  };

  const handleDeliveryFailed = async () => {
    if (!failedReason.trim()) { toast.error('Please enter a reason'); return; }
    try {
      const res = await orderService.deliveryFailed(selected.id, failedReason);
      toast.success(res?.message || 'Marked as delivery failed');
      setFailedReason(''); setModal(null); setSelected(null); refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
  };

  const handleAssignDelivery = async (vals) => {
    try {
      const res = await orderService.assignDelivery(selected.id, parseInt(vals.deliveryBoyId));
      toast.success(res?.message || 'Delivery boy assigned!');
      refetch(); setModal(null); setSelected(null);
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to assign delivery'); }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) { toast.error('Please enter a cancel reason'); return; }
    try {
      const res = await orderService.cancelOrder(selected.id, cancelReason);
      toast.success(res?.message || 'Order cancelled');
      setCancelReason(''); setModal(null); setSelected(null); refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to cancel'); }
  };

  const handleCODCollection = async () => {
    if (!codForm.delivery_boy_id || !codForm.amount || !codForm.order_ids) {
      toast.error('Please fill all required fields'); return;
    }
    try {
      const res = await orderService.markCODCollection({
        delivery_boy_id: parseInt(codForm.delivery_boy_id),
        amount:          parseFloat(codForm.amount),
        order_ids:       parseInt(codForm.order_ids),
        notes:           codForm.notes || '',
      });
      toast.success(res?.message || 'COD collection marked!');
      setCodForm({ delivery_boy_id: '', amount: '', order_ids: '', notes: '' });
      setModal(null); refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
  };

  const columns = [
    {
      key: 'uid', label: 'Order ID',
      render: v => <span className="font-mono text-medihub-primary dark:text-medihub-highlight font-medium text-xs">{v}</span>
    },
    {
      key: 'user_name', label: 'Patient', render: (v, row) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{v}</p>
          <p className="text-xs text-gray-500">{row.user_mobile}</p>
        </div>
      )
    },
    {
      key: 'items', label: 'Items', render: v => (
        <div className="flex items-center gap-1.5">
          {v?.slice(0, 2).map((item, i) => (
            item.image_url
              ? <img key={i} src={item.image_url} alt={item.name} className="w-7 h-7 rounded-lg object-cover border border-gray-200 dark:border-gray-700" />
              : <div key={i} className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center"><Package className="w-3.5 h-3.5 text-gray-400" /></div>
          ))}
          {v?.length > 2 && <span className="text-xs text-gray-400">+{v.length - 2}</span>}
          <span className="text-xs text-gray-500 ml-1">{v?.length} item{v?.length !== 1 ? 's' : ''}</span>
        </div>
      )
    },
    { key: 'total_amount', label: 'Total', render: v => <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(v)}</span> },
    {
      key: 'payment_method', label: 'Payment', render: (v, row) => (
        <div>
          <span className={`badge capitalize ${v === 'cod' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>{v}</span>
          {v === 'cod' && <p className="text-xs text-gray-400 mt-0.5">Collected: {formatCurrency(row.cod_collected || 0)}</p>}
        </div>
      )
    },
    { key: 'created_at', label: 'Date', render: v => <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(v)}</span> },
    {
      key: 'delivery_boy_name', label: 'Delivery Boy', render: (v, row) => v ? (
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center text-orange-600 dark:text-orange-400 font-bold text-xs shrink-0">{v[0]}</div>
          <div>
            <p className="text-xs font-medium text-gray-900 dark:text-white leading-tight">{v}</p>
            <p className="text-xs text-gray-400">{row.delivery_boy_mobile}</p>
          </div>
        </div>
      ) : <span className="text-xs text-gray-400 italic">Not assigned</span>
    },
    {
      key: 'status', label: 'Status', render: v => <StatusBadge status={v} dot={false} />
    },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          {!['delivered', 'cancelled'].includes(row.status) && (
            <button onClick={() => { setSelected(row); setModal('assign'); }}
              className={`p-1.5 rounded-lg transition-colors ${row.delivery_boy_id ? 'text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20' : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'}`}
              title={row.delivery_boy_id ? 'Reassign' : 'Assign Delivery'}>
              {row.delivery_boy_id ? <UserCheck className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
            </button>
          )}
          {['placed', 'approved'].includes(row.status) && (
            <button onClick={() => { setSelected(row); setCancelReason(''); setModal('cancel'); }}
              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Cancel Order">
              <X className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => { setSelected(row); setModal('detail'); }}
            className="p-1.5 hover:bg-medihub-light dark:hover:bg-medihub-dark/20 rounded-lg transition-colors text-medihub-primary" title="View Details">
            <Eye className="w-4 h-4" />
          </button>
          {row.prescriptions?.length > 0 && (
            <button onClick={() => { setSelected(row); setModal('prescription'); }}
              className="p-1.5 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors text-purple-500" title="View Prescription">
              <FileImage className="w-4 h-4" />
            </button>
          )}
        </div>
      )
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Orders</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage medicine orders and delivery assignments</p>
        </div>
        {/* <div className="flex items-center gap-2">
          <button onClick={() => setModal('cod')} className="btn-secondary flex items-center gap-1.5">
            <IndianRupee className="w-4 h-4" />COD Collection
          </button>
          <button onClick={() => { setCodHistoryBoyId(''); setModal('cod-history'); }} className="btn-secondary flex items-center gap-1.5">
            <FileText className="w-4 h-4" />Collection History
          </button>
        </div> */}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Orders',     value: pagination.total || orders.length || 0,                                          color: 'text-gray-900 dark:text-white',        bg: '' },
          { label: 'Out for Delivery', value: orders.filter(o => o.status === 'out-for-delivery' || o.status === 'assigned').length,                      color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/10' },
          { label: 'Delivered',        value: orders.filter(o => o.status === 'delivered').length,                             color: 'text-green-700 dark:text-green-400',   bg: 'bg-green-50 dark:bg-green-900/10' },
          { label: 'Cancelled',        value: orders.filter(o => o.status === 'cancelled').length,                             color: 'text-red-600 dark:text-red-400',       bg: 'bg-red-50 dark:bg-red-900/10' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`card p-4 text-center ${bg}`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters} onChange={onFilterChange} onReset={onReset}
        searchPlaceholder="Search by order ID, patient name or mobile…"
        fields={FILTER_FIELDS} activeCount={activeCount}
      />

      {/* Table */}
      <Table
        title={`Orders (${filtered.length}${activeCount ? ` of ${orders.length}` : ''})`}
        columns={columns} data={filtered} loading={loading} exportFilename="orders"
      />

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between px-1">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Page {pagination.page} of {pagination.total_pages} · {pagination.total} total orders
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => p - 1)} disabled={!pagination.has_prev}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft className="w-4 h-4" />Prev
            </button>
            <button
              onClick={() => setPage(p => p + 1)} disabled={!pagination.has_next}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Next<ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Assign Delivery Modal */}
      <Modal isOpen={modal === 'assign'} onClose={() => { setModal(null); setSelected(null); }}
        title={selected?.delivery_boy_id ? 'Reassign Delivery Boy' : 'Assign Delivery Boy'} size="md">
        {selected && (
          <AssignDeliveryForm
            order={selected} deliveryBoys={activeDeliveryBoys || []}
            onSubmit={handleAssignDelivery}
            onClose={() => { setModal(null); setSelected(null); }}
          />
        )}
      </Modal>

      {/* Order Detail Modal */}
      <Modal isOpen={modal === 'detail'} onClose={() => setModal(null)} title={`Order ${selected?.uid}`} size="md">
        {selected && (
          <div className="space-y-4">
            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Patient',    value: selected.user_name },
                { label: 'Mobile',     value: selected.user_mobile || selected.mobile },
                { label: 'Total',      value: formatCurrency(selected.total_amount) },
                { label: 'Payment',    value: selected.payment_method?.toUpperCase() },
                { label: 'COD Collected', value: formatCurrency(selected.cod_collected || 0) },
                { label: 'Order Date', value: formatDate(selected.created_at) },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-0.5">{value || '—'}</p>
                </div>
              ))}
            </div>

            {/* Address */}
            <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Delivery Address</p>
                <p className="text-sm text-gray-800 dark:text-gray-200">
                  {[selected.address_line, selected.city, selected.state, selected.pincode].filter(Boolean).join(', ')}
                </p>
                {selected.mobile && <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Phone className="w-3 h-3" />{selected.mobile}</p>}
              </div>
            </div>

            {/* Note */}
            {selected.note && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-0.5">Note</p>
                <p className="text-sm text-blue-800 dark:text-blue-300">{selected.note}</p>
              </div>
            )}

            {/* Cancel Reason */}
            {selected.cancel_reason && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-0.5">Cancel Reason · {formatDate(selected.cancelled_at)}</p>
                <p className="text-sm text-red-800 dark:text-red-300">{selected.cancel_reason}</p>
              </div>
            )}

            {/* Delivery Boy */}
            {selected.delivery_boy_name && (
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 mb-1 flex items-center gap-1"><Truck className="w-3.5 h-3.5" />Delivery Boy</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{selected.delivery_boy_name}</p>
                <p className="text-xs text-gray-500">{selected.delivery_boy_mobile}</p>
              </div>
            )}

            {/* Items */}
            <div>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Order Items</p>
              <div className="space-y-2">
                {selected.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl">
                    {item.image_url
                      ? <img src={item.image_url} alt={item.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
                      : <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0"><Package className="w-5 h-5 text-gray-400" /></div>
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{item.type?.replace(/_/g, ' ')} · Qty: {item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <DeliveryTimeline currentStatus={selected.status} />

            {/* Status Update */}
            {getAllowedStatuses(selected.status).length > 0 && (
              <div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Update Status</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {getAllowedStatuses(selected.status).map(s => (
                    <button key={s} onClick={() => handleStatusUpdate(selected.id, s)}
                      className={`py-2 px-3 rounded-lg text-xs font-medium capitalize transition-colors flex items-center justify-center gap-1.5 ${
                        selected.status === s
                          ? `${STATUS_COLORS[s]} shadow-sm`
                          : `${STATUS_COLORS[s]} opacity-80 hover:opacity-100`
                      }`}>
                      {STATUS_ICONS[s]}{s.replace(/-/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delivery Failed Modal */}
      <Modal isOpen={modal === 'delivery-failed'} onClose={() => setModal('detail')} title="Mark Delivery Failed" size="sm">
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-sm">
            <p className="text-gray-500">Order: <span className="font-semibold text-gray-900 dark:text-white">{selected?.uid}</span></p>
            <p className="text-gray-500 mt-0.5">Patient: <span className="font-medium text-gray-800 dark:text-gray-200">{selected?.user_name}</span></p>
          </div>
          <FormField label="Reason" required>
            <textarea
              value={failedReason}
              onChange={e => setFailedReason(e.target.value)}
              className="input-field resize-none" rows={3}
              placeholder="e.g. Customer not available…"
            />
          </FormField>
          <div className="flex justify-end gap-3">
            <button onClick={() => setModal('detail')} className="btn-secondary">Back</button>
            <button onClick={handleDeliveryFailed}
              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-lg transition-colors">
              Confirm Failed
            </button>
          </div>
        </div>
      </Modal>

      {/* Invoice Modal */}
      {invoiceOrder && <InvoiceModal order={invoiceOrder} onClose={() => setInvoiceOrder(null)} />}

      {/* Prescription Modal */}
      <Modal isOpen={modal === 'prescription'} onClose={() => { setModal(null); setSelected(null); }} title={`Prescriptions — ${selected?.uid}`} size="md">
        {selected && (
          <div className="space-y-3">
            {selected.prescriptions?.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No prescriptions found</p>
            ) : (
              selected.prescriptions?.map((p, i) => {
                const isPdf = p.file_url?.toLowerCase().endsWith('.pdf');
                return (
                  <div key={p.id} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/50">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Prescription {i + 1}</span>
                      <a href={p.file_url} target="_blank" rel="noreferrer"
                        className="text-xs text-medihub-primary dark:text-medihub-highlight font-medium hover:underline flex items-center gap-1">
                        <FileImage className="w-3 h-3" />Open
                      </a>
                    </div>
                    {isPdf ? (
                      <div className="flex items-center justify-center gap-3 p-6 bg-red-50 dark:bg-red-900/10">
                        <FileText className="w-10 h-10 text-red-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">PDF Document</p>
                          <a href={p.file_url} target="_blank" rel="noreferrer"
                            className="text-xs text-medihub-primary dark:text-medihub-highlight hover:underline">Click to open PDF</a>
                        </div>
                      </div>
                    ) : (
                      <img src={p.file_url} alt={`Prescription ${i + 1}`}
                        className="w-full max-h-80 object-contain bg-gray-100 dark:bg-gray-800" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </Modal>

      {/* Cancel Order Modal */}
      <Modal isOpen={modal === 'cancel'} onClose={() => setModal(null)} title="Cancel Order" size="sm">
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-sm">
            <p className="text-gray-500">Order: <span className="font-semibold text-gray-900 dark:text-white">{selected?.uid}</span></p>
            <p className="text-gray-500 mt-0.5">Patient: <span className="font-medium text-gray-800 dark:text-gray-200">{selected?.user_name}</span></p>
          </div>
          <FormField label="Cancel Reason" required>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              className="input-field resize-none" rows={3}
              placeholder="e.g. delivery boy not available…"
            />
          </FormField>
          <div className="flex justify-end gap-3">
            <button onClick={() => setModal(null)} className="btn-secondary">Back</button>
            <button onClick={handleCancelOrder}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors">
              Cancel Order
            </button>
          </div>
        </div>
      </Modal>

      {/* COD Collection Modal */}
      <Modal isOpen={modal === 'cod'} onClose={() => setModal(null)} title="Mark COD Collection" size="sm">
        <div className="space-y-4">
          <FormField label="Delivery Boy" required>
            <select value={codForm.delivery_boy_id} onChange={e => setCodForm(p => ({ ...p, delivery_boy_id: e.target.value }))} className="input-field">
              <option value="">Select delivery boy</option>
              {(activeDeliveryBoys || []).map(b => <option key={b.id} value={b.id}>{b.name} — {b.mobile}</option>)}
            </select>
          </FormField>
          <FormField label="Order ID" required>
            <input type="number" value={codForm.order_ids} onChange={e => setCodForm(p => ({ ...p, order_ids: e.target.value }))} className="input-field" placeholder="Order ID" />
          </FormField>
          <FormField label="Amount" required>
            <input type="number" step="0.01" value={codForm.amount} onChange={e => setCodForm(p => ({ ...p, amount: e.target.value }))} className="input-field" placeholder="e.g. 270" />
          </FormField>
          <FormField label="Notes">
            <input value={codForm.notes} onChange={e => setCodForm(p => ({ ...p, notes: e.target.value }))} className="input-field" placeholder="e.g. Amount received." />
          </FormField>
          <div className="flex justify-end gap-3">
            <button onClick={() => setModal(null)} className="btn-secondary">Cancel</button>
            <button onClick={handleCODCollection} className="btn-primary flex items-center gap-1.5">
              <IndianRupee className="w-4 h-4" />Mark Collected
            </button>
          </div>
        </div>
      </Modal>

      {/* COD Collection History Modal */}
      <Modal isOpen={modal === 'cod-history'} onClose={() => setModal(null)} title="COD Collection History" size="md">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <select value={codHistoryBoyId} onChange={e => setCodHistoryBoyId(e.target.value)} className="input-field flex-1">
              <option value="">Select delivery boy</option>
              {(activeDeliveryBoys || []).map(b => <option key={b.id} value={b.id}>{b.name} — {b.mobile}</option>)}
            </select>
          </div>
          {codHistoryBoyId && (
            Array.isArray(codHistory) && codHistory.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No collection history found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      {['Order ID', 'Amount', 'Notes', 'Date'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {(codHistory || []).map((c, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 py-2.5 font-mono text-xs text-medihub-primary dark:text-medihub-highlight">{c.order_id || c.order_ids}</td>
                        <td className="px-3 py-2.5 font-semibold text-green-600 dark:text-green-400">{formatCurrency(c.amount)}</td>
                        <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{c.notes || '—'}</td>
                        <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(c.created_at || c.date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Orders;
